import { Client } from '@sr-sdks/api-sdk-axios';
import { Alert } from '@sr/ui-library';
import { useState } from 'react';
import AppMultiStepsModal from '../../../components/AppMultiStepsModal';
import { ConfirmClients as ConfirmClientsComponent } from '../../../components/ConfirmClients';
import { ReviewClients as ReviewClientsComponent } from '../../../components/ReviewClients';
import { CompletedOnboardingStep } from '../ConfirmClientsSteps/CompletedOnboardingStep';
import GetStartedOnboardingStep from '../ConfirmClientsSteps/GetStartedOnboardingStep';

interface UnconfirmedClientsAlertProps {
  onConfirm: () => void;
  workspaceName: string;
}

export const UnconfirmedClientsAlert = ({
  onConfirm,
  workspaceName,
}: UnconfirmedClientsAlertProps) => (
  <div className="flex h-auto items-center self-stretch xl:mx-auto">
    <Alert
      actionButtonProps={{
        children: 'Confirm Now',
        onClick: onConfirm,
      }}
      hasActionButton
      variant="info"
    >
      Screenrights needs all agents to confirm the representation status of
      their clients. We have detected one or more clients are unconfirmed on
      your workspace: <strong>{workspaceName}</strong>. Click &apos;Confirm
      Now&apos; to start the confirmation process.
    </Alert>
  </div>
);

interface ConfirmClientsModalProps {
  clients: Client[];
  isOpen: boolean;
  onClientsChange: (clients: Client[]) => void;
  onClose: () => void;
  onSaved?: () => void;
  workspaceId: string;
}

export const ConfirmClientsModal = ({
  clients,
  isOpen,
  onClientsChange,
  onClose,
  onSaved,
  workspaceId,
}: ConfirmClientsModalProps) => {
  // Blocks closing until the saves settle, so a failure still reaches Try Again.
  const [isSaving, setIsSaving] = useState(false);

  return (
    <AppMultiStepsModal
      isOpen={isOpen}
      steps={[
        {
          classes: 'intro',
          component: <GetStartedOnboardingStep />,
          nextButtonText: 'Continue',
          showPrevious: false,
          title: 'Get Started',
        },
        {
          classes: 'steps confirm-clients',
          component: (
            <ConfirmClientsComponent
              clientWorkspaceId={workspaceId}
              unconfirmedClients={clients}
              onUpdatedClients={onClientsChange}
            />
          ),
          nextButtonText: 'Proceed to Review',
          showPrevious: false,
          title: 'Confirm Clients',
        },
        {
          classes: 'steps review-clients',
          component: (
            <ReviewClientsComponent
              clientWorkspaceId={workspaceId}
              clientsList={clients}
            />
          ),
          nextButtonText: 'Save & Continue',
          showPrevious: true,
          title: 'Review Clients',
        },
        {
          classes: 'steps done',
          component: (
            <CompletedOnboardingStep
              clientsList={clients}
              onSaved={onSaved}
              onSavingChange={setIsSaving}
            />
          ),
          isLoading: isSaving,
          nextButtonText: 'Close',
          showPrevious: false,
          title: 'Completed',
        },
      ]}
      onCloseClick={onClose}
    />
  );
};
